// src/client/screens/borrower/LoanApplyScreen.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { LOAN_TERM_DAYS, type LoanTermDays } from '@/constants/config';
import { useCreditScore } from '@client/hooks/useCreditScore';
import { kwachaToNgwee, type Ngwee } from '@/lib/types';
import { ngweeToKwachaNumber } from '@client/utils/currency';
import { maxLoanForTier } from '@client/utils/creditScoreUtils';
import { computeAllInCost, INTEREST_RATES } from '@client/utils/loanUtils';
import { saveLoanDraft } from '@client/lib/loanDraft';
import Button from '@client/components/Button';
import { useToast } from '@client/components/ToastProvider';

const MIN_LOAN_KWACHA = 50; // K50 — absolute floor; first-time cap is enforced server-side

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

export default function LoanApplyScreen() {
  const { data: scoreData } = useCreditScore();
  const navigate = useNavigate();
  const toast = useToast();

  const tier = scoreData?.tier ?? 'new';
  const maxKwacha = ngweeToKwachaNumber(maxLoanForTier(tier));

  // Default to a comfortable starting point inside the user's range.
  const initial = Math.min(2000, Math.max(MIN_LOAN_KWACHA, maxKwacha));
  const [amount, setAmount] = useState<number>(initial);
  const [term, setTerm] = useState<LoanTermDays>(30);

  const principalNgwee = kwachaToNgwee(amount) as Ngwee;
  const cost = computeAllInCost(principalNgwee, term);

  // Quick-pick chips — at most four, all within the user's range.
  const QUICK = [500, 1000, 2000, 5000].filter((v) => v >= MIN_LOAN_KWACHA && v <= maxKwacha);

  const handleApply = () => {
    if (amount < MIN_LOAN_KWACHA) {
      toast.show(`Minimum loan amount is K${MIN_LOAN_KWACHA.toLocaleString()}`, 'error');
      return;
    }
    if (amount > maxKwacha) {
      toast.show(`Maximum for your tier is K${maxKwacha.toLocaleString()}`, 'error');
      return;
    }
    saveLoanDraft({ principal: principalNgwee, term });
    navigate(ROUTES.loanConfirm);
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--c-bg)',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 480,
      margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 20px 8px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
        <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(-1)}>
          <ChevronLeft size={18} />
        </button>
        <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--c-text-muted)' }}>
          borrow
        </span>
      </div>

      <div style={{ padding: '24px 28px', flex: 1 }}>
        <h1 style={{
          fontFamily: 'var(--ff-display)',
          fontSize: 38,
          lineHeight: 1,
          color: 'var(--c-text)',
          letterSpacing: -1,
          fontWeight: 400,
          margin: 0,
        }}>
          How much<br />do you need?
        </h1>

        {/* Big amount display */}
        <div style={{
          marginTop: 36,
          padding: '28px 20px',
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 20,
          textAlign: 'center',
        }}>
          <div className="eyebrow">Loan amount</div>
          <div style={{
            fontFamily: 'var(--ff-display)',
            fontSize: 86,
            color: 'var(--c-text)',
            letterSpacing: -3,
            lineHeight: 1,
            marginTop: 8,
            fontWeight: 400,
          }}>
            K{amount.toLocaleString()}
          </div>
        </div>

        {/* Slider */}
        <div style={{ marginTop: 28, padding: '0 4px' }}>
          <input
            type="range"
            min={MIN_LOAN_KWACHA}
            max={Math.max(MIN_LOAN_KWACHA, maxKwacha)}
            step={50}
            value={amount}
            onChange={(e) => setAmount(+e.target.value)}
            aria-label="Loan amount"
            style={{ width: '100%', accentColor: 'var(--c-accent)', height: 4 }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 8,
            fontFamily: 'var(--ff-mono)',
            fontSize: 11,
            color: 'var(--c-text-muted)',
          }}>
            <span>K{MIN_LOAN_KWACHA.toLocaleString()}</span>
            <span>K{maxKwacha.toLocaleString()}</span>
          </div>
        </div>

        {/* Quick-pick chips */}
        {QUICK.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {QUICK.map((v) => {
              const selected = amount === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(v)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: selected ? 'var(--c-text)' : 'transparent',
                    color: selected ? 'var(--c-bg)' : 'var(--c-text-sec)',
                    border: `1px solid ${selected ? 'var(--c-text)' : 'var(--c-border)'}`,
                    fontFamily: 'var(--ff-sys)',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  K{v.toLocaleString()}
                </button>
              );
            })}
          </div>
        )}

        {/* Term grid */}
        <div style={{ marginTop: 28 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Repayment term</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {LOAN_TERM_DAYS.map((t) => {
              const selected = term === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTerm(t)}
                  style={{
                    padding: '14px 6px',
                    borderRadius: 14,
                    background: selected ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                    border: `1px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{
                    fontFamily: 'var(--ff-display)',
                    fontSize: 26,
                    color: 'var(--c-text)',
                    lineHeight: 1,
                    letterSpacing: -0.5,
                  }}>
                    {t}
                  </div>
                  <div style={{
                    fontSize: 10,
                    color: 'var(--c-text-muted)',
                    marginTop: 4,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}>
                    days
                  </div>
                  <div style={{
                    fontFamily: 'var(--ff-mono)',
                    fontSize: 10,
                    color: selected ? 'var(--c-accent)' : 'var(--c-text-dim)',
                    marginTop: 6,
                  }}>
                    {Math.round(INTEREST_RATES[t] * 100)}%
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cost breakdown */}
        <div style={{
          marginTop: 24,
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 16,
          padding: 18,
        }}>
          {[
            ['Principal', `K${amount.toLocaleString()}.00`],
            [`Interest · ${Math.round(INTEREST_RATES[term] * 100)}% / ${term}d`, `K${ngweeToKwachaNumber(cost.interest).toLocaleString()}.00`],
            ['Processing fee', `K${ngweeToKwachaNumber(cost.processingFee).toLocaleString()}.00`],
          ].map(([label, value]) => (
            <div key={label} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 0',
              fontSize: 13,
            }}>
              <span style={{ color: 'var(--c-text-muted)' }}>{label}</span>
              <span style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-mono)' }}>{value}</span>
            </div>
          ))}
          <div style={{ height: 1, background: 'var(--c-border-lt)', margin: '10px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 13, color: 'var(--c-text)', fontWeight: 500 }}>You&apos;ll repay</span>
            <span style={{
              fontFamily: 'var(--ff-display)',
              fontSize: 24,
              color: 'var(--c-text)',
              letterSpacing: -0.5,
            }}>
              K{ngweeToKwachaNumber(cost.totalDue).toLocaleString()}.00
            </span>
          </div>
        </div>
      </div>

      <div style={{ padding: '14px 24px 28px' }}>
        <Button block size="lg" onClick={handleApply} rightIcon={<ArrowRight size={18} />}>
          Review loan
        </Button>
      </div>
    </div>
  );
}
